import { ApiErrorCode, withApiErrorHandler } from '@/modules/common/api/errors';
import {
  mapIntegrationLinksListFromDTO,
  mergeIntegrations,
} from '@/modules/common/mappers/integration.mapper';
import type {
  IntegrationInfoResponse,
  IntegrationLinksResponse,
} from '@/modules/common/types/dto/response/integration-response';
import { EntityType } from '@/modules/common/types/entity-type';
import { axiosInstance } from '@/utils/axios';

import { mapEntityIntegrationsToProjectIntegrations } from '../mappers/project-integration.mapper';
import { ProjectIntegration } from '../types/project-integration';

/**
 * Retrieves all available integrations with their unlinked external projects.
 * Optionally merges with selected integrations from project details.
 *
 * @param selectedIntegrations - Optional integration data from project details with selected external projects
 * @returns Integrations with complete project lists and selections (if provided)
 */
export const getAvailableIntegrations = async (
  selectedIntegrations?: IntegrationInfoResponse[]
): Promise<ProjectIntegration[]> => {
  const endpoint = '/api/entity-integration-links/unlinked';

  const response = await withApiErrorHandler(endpoint, ApiErrorCode.INTEGRATIONS_LOAD_FAILED, () =>
    axiosInstance.get<{
      data: IntegrationLinksResponse[];
      error?: { status: number; message: string; code: string };
    }>(endpoint, {
      params: {
        entityType: EntityType.Project,
      },
    })
  );

  const unlinkedIntegrations = mapIntegrationLinksListFromDTO(response);

  const finalIntegrations = selectedIntegrations
    ? mergeIntegrations(unlinkedIntegrations, selectedIntegrations)
    : unlinkedIntegrations;

  return mapEntityIntegrationsToProjectIntegrations(finalIntegrations).sort((a, b) =>
    a.type.localeCompare(b.type)
  );
};
